import { useEffect, useState, useRef } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { generateOTP } from 'client/one-stop-checkin/one-stop-checkin.client'
import { useRequiredParams } from 'hooks/params.hook'
import { OtpResponseSchema } from 'types/utils/model'
import { OTP_ROLES, ZOD_ERROR } from 'utils/constants'

export const useTokenGenerator = () => {
  const [otpCode, setOtpCode] = useState('')
  const [timeLeft, setTimeLeft] = useState(0)
  const [duration, setDuration] = useState(0) // in seconds
  const [loading, setLoading] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const rawOtp = useLoaderData()
  const navigate = useNavigate()
  const { type } = useRequiredParams('type')

  const otpTypeLabel = OTP_ROLES[type]?.label || type

  const calculateTimeLeft = (ttl: number) => {
    const now = Date.now()
    const ttlInMs = ttl * 1000
    const secondsLeft = Math.max(Math.floor((ttlInMs - now) / 1000), 0)
    return secondsLeft
  }

  const fetchOtp = async () => {
    try {
      setLoading(true)
      const response = await generateOTP(type)
      const newTimeLeft = calculateTimeLeft(response.ttl)
      setOtpCode(response.code)
      setTimeLeft(newTimeLeft)
      setDuration(newTimeLeft)
    } catch (error) {
      console.error('Error obteniendo el OTP:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const result = OtpResponseSchema.safeParse(rawOtp)
    if (!result.success) {
      throw Error(ZOD_ERROR)
    }

    const initialTimeLeft = calculateTimeLeft(result.data.ttl)

    setOtpCode(result.data.code)
    setTimeLeft(initialTimeLeft)
    setDuration(initialTimeLeft)

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          fetchOtp()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const progressValue = duration === 0 ? 0 : (timeLeft / duration) * 100
  const durationInMinutes = Math.round(duration / 60)

  const generateNewOTP = () => {
    fetchOtp()
  }

  const goBack = () => {
    navigate(-1)
  }

  return {
    otpCode,
    timeLeft,
    progressValue,
    durationInMinutes,
    loading,
    otpTypeLabel,
    actions: { generateNewOTP, goBack }
  }
}
